import * as qs from 'qs'
import _ from 'lodash'
import type Passport from '@patsnap-utils/passport/src/passport'
import { CommonApis } from './apis/common.api'
import type { HeaderService } from '@patsnap-utils/biz'
import { NeedAddTrialActionType, type IAddTrialExtendBody } from './apis/common.api.types'
import { isAxiosError } from './exception'
import { Site, SLD } from '@patsnap-utils/biz'
import { addTrialDeferred } from '@eureka/utils/account'

class AddTrialService {
    passport: Passport | void = undefined

    ENABLE_AUTO_ADD_TRIAL = true

    async addTrial(headerService: HeaderService, passport: Passport | void) {
        try {
            if (this.isAccountStatusPage()) {
                console.debug('[AddTrial] Skip product onboarding check on account-status page')
                return
            }

            this.passport = passport
            const grantedProductIds: string[] | undefined = headerService.account?._info.granted_product_ids
            if (grantedProductIds === undefined) {
                throw new Error('Granted_product_ids is undefined!')
            }

            const extendBody = this.getAddTrialExtendBody()
            const empowerType = this.getEmpowerType(extendBody.from)
            const needAddTrialData = await CommonApis.needAddTrial(empowerType || '', extendBody)
            const actionType = _.get(needAddTrialData, 'data.action_type', '') as NeedAddTrialActionType | string

            this.ENABLE_AUTO_ADD_TRIAL = false
            console.debug('[AddTrial] product-onboarding action:', { actionType, empowerType })

            switch (actionType) {
            case NeedAddTrialActionType.DIRECT_LOGIN:
                break
            case NeedAddTrialActionType.GRANT_INDIVIDUAL_TENANT_PRIVILEGE:
                this.refreshGrantedPrivilege()
                break
            case NeedAddTrialActionType.SWITCH_TO_INDIVIDUAL_TENANT:
            case NeedAddTrialActionType.SWITCH_AND_GRANT_PRIVILEGE:
            case NeedAddTrialActionType.CREATE_INDIVIDUAL_TENANT:
            case NeedAddTrialActionType.CONTACT_ADMIN:
                this.redirectToAccountStatusPage()
                break
            default:
                break
            }
        } catch (error) {
            console.error('[AddTrial]', error)
            if (this.isNoPermissionError(error)) {
                window.location.href = new Site({ sld: SLD.EUREKA, pathname: '403/' }).href
            }
        } finally {
            this.ENABLE_AUTO_ADD_TRIAL = false
            addTrialDeferred.resolve(true)
        }
    }

    refreshGrantedPrivilege() {
        localStorage.removeItem('patsnap_header_account_info')
        if (!this.passport) throw new Error('passport is not setup!')
        this.passport.removeLocal()
        window.location.reload()
    }

    isAccountStatusPage() {
        if (typeof window === 'undefined') return false
        return window.location.pathname.includes('account-status') ||
            window.location.hash.includes('account-status')
    }

    redirectToAccountStatusPage() {
        if (typeof window === 'undefined') return
        const pathName = new Site({ sld: SLD.EUREKA, pathname: 'account-status/' }).href
        window.location.href = `${pathName}?account_status_from=${encodeURIComponent(window.location.href)}`
    }

    isNoPermissionError(error: unknown) {
        const record = error as Record<string, any>
        const codes = [
            record?.error_code,
            record?.data?.error_code,
            record?.response?.data?.error_code,
            record?.response?.data?.data?.error_code,
        ].filter(code => code !== undefined && code !== null)

        return codes.some(code => String(code).includes('90112055'))
    }

    isMobileDevice() {
        return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
            navigator.userAgent
        )
    }

    getAddTrialExtendBody(): IAddTrialExtendBody {
        const hash = window.location.hash
        const queryString = hash.includes('?') ? '?' + hash.split('?')[1] : window.location.search

        const query = qs.parse(queryString, {
            ignoreQueryPrefix: true,
        })
        // @ts-ignore
        return {
            ...query,
            media_type: this.isMobileDevice() ? 'Mobile' : '',
        }
    }

    getEmpowerType(from?: string) {
        if (from === 'discovery_seo_company') return 'INSIGHTS'
        if (from?.includes('eureka-seo-material')) return 'MATERIAL'
        if (from === 'feishu') return 'FEISHU'
        return 'AIR'
    }
}

export const addTrialService = new AddTrialService()

export function errorHandleIgnore403(e: Error) {
    return isAxiosError(e) && e.response?.status === 403
}
